import { AccessRuleMethod } from './model'

export interface ApiPathConfig {
  path: string
  methods: AccessRuleMethod[]
  description?: string
  isFullAccess?: boolean
}

export const ALL_METHODS: AccessRuleMethod[] = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE']

export interface ServiceApiConfig {
  displayName: string
  paths: ApiPathConfig[]
}

export type ApiAccessRulesConfig = Record<string, ServiceApiConfig>

/**
 * Configuration for predefined API paths by service.
 * This is used to provide autocomplete suggestions for access rules.
 *
 * Services are grouped into:
 * - OpenStack standard services (compute, network, image, volumev3, identity, etc.)
 * - Platform9 custom services (hamgr, resmgr, mors, etc.)
 *
 * Note: Users can still enter custom paths not in this list (free-text fallback).
 */
export const apiAccessRulesConfig: ApiAccessRulesConfig = {
  // ============================================
  // OpenStack Standard Services
  // ============================================

  compute: {
    displayName: 'Compute (Nova)',
    paths: [
      {
        path: '/v2.1/{project_id}/**',
        methods: ALL_METHODS,
        description: 'Full access to all Compute APIs',
        isFullAccess: true,
      },
      {
        path: '/v2.1/{project_id}/servers',
        methods: ['GET', 'POST'],
        description: 'List/Create servers',
      },
      {
        path: '/v2.1/{project_id}/servers/{server_id}',
        methods: ALL_METHODS,
        description: 'Server operations',
      },
      {
        path: '/v2.1/{project_id}/flavors',
        methods: ['GET', 'POST'],
        description: 'List/Create flavors',
      },
      {
        path: '/v2.1/{project_id}/flavors/{flavor_id}',
        methods: ALL_METHODS,
        description: 'Flavor operations',
      },
      {
        path: '/v2.1/{project_id}/os-keypairs',
        methods: ['GET', 'POST'],
        description: 'SSH keypairs',
      },
      {
        path: '/v2.1/{project_id}/os-keypairs/{keypair_name}',
        methods: ['GET', 'DELETE'],
        description: 'Keypair operations',
      },
      { path: '/v2.1/{project_id}/os-hypervisors', methods: ['GET'], description: 'Hypervisors' },
      {
        path: '/v2.1/{project_id}/os-hypervisors/{hypervisor_id}',
        methods: ['GET'],
        description: 'Hypervisor details',
      },
      {
        path: '/v2.1/{project_id}/os-aggregates',
        methods: ['GET', 'POST'],
        description: 'Host aggregates',
      },
      {
        path: '/v2.1/{project_id}/os-aggregates/{aggregate_id}',
        methods: ALL_METHODS,
        description: 'Aggregate operations',
      },
      {
        path: '/v2.1/{project_id}/os-availability-zone',
        methods: ['GET'],
        description: 'Availability zones',
      },
      {
        path: '/v2.1/{project_id}/os-server-groups',
        methods: ['GET', 'POST'],
        description: 'Server groups',
      },
      {
        path: '/v2.1/{project_id}/os-server-groups/{server_group_id}',
        methods: ['GET', 'DELETE'],
        description: 'Server group operations',
      },
      {
        path: '/v2.1/{project_id}/os-quota-sets/{tenant_id}',
        methods: ['GET', 'PUT'],
        description: 'Quotas',
      },
      { path: '/v2.1/{project_id}/os-services', methods: ['GET'], description: 'Services' },
      { path: '/v2.1/{project_id}/os-migrations', methods: ['GET'], description: 'Migrations' },
    ],
  },

  network: {
    displayName: 'Network (Neutron)',
    paths: [
      {
        path: '/v2.0/**',
        methods: ALL_METHODS,
        description: 'Full access to all Network APIs',
        isFullAccess: true,
      },
      { path: '/v2.0/networks', methods: ['GET', 'POST'], description: 'Networks' },
      {
        path: '/v2.0/networks/{network_id}',
        methods: ALL_METHODS,
        description: 'Network operations',
      },
      { path: '/v2.0/subnets', methods: ['GET', 'POST'], description: 'Subnets' },
      { path: '/v2.0/subnets/{subnet_id}', methods: ALL_METHODS, description: 'Subnet operations' },
      { path: '/v2.0/ports', methods: ['GET', 'POST'], description: 'Ports' },
      { path: '/v2.0/ports/{port_id}', methods: ALL_METHODS, description: 'Port operations' },
      { path: '/v2.0/routers', methods: ['GET', 'POST'], description: 'Routers' },
      { path: '/v2.0/routers/{router_id}', methods: ALL_METHODS, description: 'Router operations' },
      { path: '/v2.0/floatingips', methods: ['GET', 'POST'], description: 'Floating IPs' },
      {
        path: '/v2.0/floatingips/{floatingip_id}',
        methods: ALL_METHODS,
        description: 'Floating IP operations',
      },
      { path: '/v2.0/security-groups', methods: ['GET', 'POST'], description: 'Security groups' },
      {
        path: '/v2.0/security-groups/{security_group_id}',
        methods: ALL_METHODS,
        description: 'Security group operations',
      },
      {
        path: '/v2.0/security-group-rules',
        methods: ['GET', 'POST'],
        description: 'Security group rules',
      },
      {
        path: '/v2.0/security-group-rules/{rule_id}',
        methods: ['GET', 'DELETE'],
        description: 'Security group rule operations',
      },
      { path: '/v2.0/quotas/{tenant_id}', methods: ['GET', 'PUT'], description: 'Quotas' },
      { path: '/v2.0/rbac-policies', methods: ['GET', 'POST'], description: 'RBAC policies' },
      {
        path: '/v2.0/rbac-policies/{policy_id}',
        methods: ALL_METHODS,
        description: 'RBAC policy operations',
      },
      { path: '/v2.0/agents', methods: ['GET'], description: 'Agents' },
    ],
  },

  image: {
    displayName: 'Image (Glance)',
    paths: [
      {
        path: '/v2/**',
        methods: ALL_METHODS,
        description: 'Full access to all Image APIs',
        isFullAccess: true,
      },
      { path: '/v2/images', methods: ['GET', 'POST'], description: 'Images' },
      { path: '/v2/images/{image_id}', methods: ALL_METHODS, description: 'Image operations' },
    ],
  },

  volumev3: {
    displayName: 'Block Storage (Cinder)',
    paths: [
      {
        path: '/v3/{project_id}/**',
        methods: ALL_METHODS,
        description: 'Full access to all Block Storage APIs',
        isFullAccess: true,
      },
      { path: '/v3/{project_id}/volumes', methods: ['GET', 'POST'], description: 'Volumes' },
      {
        path: '/v3/{project_id}/volumes/{volume_id}',
        methods: ALL_METHODS,
        description: 'Volume operations',
      },
      { path: '/v3/{project_id}/snapshots', methods: ['GET', 'POST'], description: 'Snapshots' },
      {
        path: '/v3/{project_id}/snapshots/{snapshot_id}',
        methods: ALL_METHODS,
        description: 'Snapshot operations',
      },
      { path: '/v3/{project_id}/types', methods: ['GET', 'POST'], description: 'Volume types' },
      {
        path: '/v3/{project_id}/types/{volume_type_id}',
        methods: ALL_METHODS,
        description: 'Volume type operations',
      },
      {
        path: '/v3/{project_id}/os-quota-sets/{tenant_id}',
        methods: ['GET', 'PUT'],
        description: 'Quotas',
      },
      { path: '/v3/{project_id}/backups', methods: ['GET', 'POST'], description: 'Backups' },
      {
        path: '/v3/{project_id}/backups/{backup_id}',
        methods: ['GET', 'DELETE'],
        description: 'Backup operations',
      },
    ],
  },

  identity: {
    displayName: 'Identity (Keystone)',
    paths: [
      {
        path: '/v3/**',
        methods: ALL_METHODS,
        description: 'Full access to all Identity APIs',
        isFullAccess: true,
      },
      { path: '/v3/auth/tokens', methods: ['GET', 'POST', 'DELETE'], description: 'Tokens' },
      { path: '/v3/projects', methods: ['GET', 'POST'], description: 'Projects' },
      {
        path: '/v3/projects/{project_id}',
        methods: ALL_METHODS,
        description: 'Project operations',
      },
      { path: '/v3/users', methods: ['GET', 'POST'], description: 'Users' },
      { path: '/v3/users/{user_id}', methods: ALL_METHODS, description: 'User operations' },
      { path: '/v3/roles', methods: ['GET', 'POST'], description: 'Roles' },
      { path: '/v3/roles/{role_id}', methods: ALL_METHODS, description: 'Role operations' },
      { path: '/v3/domains', methods: ['GET', 'POST'], description: 'Domains' },
      { path: '/v3/domains/{domain_id}', methods: ALL_METHODS, description: 'Domain operations' },
      { path: '/v3/groups', methods: ['GET', 'POST'], description: 'Groups' },
      { path: '/v3/groups/{group_id}', methods: ALL_METHODS, description: 'Group operations' },
      { path: '/v3/credentials', methods: ['GET', 'POST'], description: 'Credentials' },
      {
        path: '/v3/credentials/{credential_id}',
        methods: ALL_METHODS,
        description: 'Credential operations',
      },
      { path: '/v3/role_assignments', methods: ['GET'], description: 'Role assignments' },
      { path: '/v3/regions', methods: ['GET', 'POST'], description: 'Regions' },
      { path: '/v3/regions/{region_id}', methods: ALL_METHODS, description: 'Region operations' },
      { path: '/v3/services', methods: ['GET', 'POST'], description: 'Services' },
      { path: '/v3/endpoints', methods: ['GET', 'POST'], description: 'Endpoints' },
    ],
  },

  placement: {
    displayName: 'Placement',
    paths: [
      {
        path: '/**',
        methods: ALL_METHODS,
        description: 'Full access to all Placement APIs',
        isFullAccess: true,
      },
      { path: '/resource_providers', methods: ['GET', 'POST'], description: 'Resource providers' },
      {
        path: '/resource_providers/{uuid}',
        methods: ALL_METHODS,
        description: 'Resource provider operations',
      },
      {
        path: '/allocations/{consumer_uuid}',
        methods: ALL_METHODS,
        description: 'Consumer allocations',
      },
      { path: '/usages', methods: ['GET'], description: 'Usages' },
      { path: '/resource_classes', methods: ['GET', 'POST'], description: 'Resource classes' },
      { path: '/traits', methods: ['GET'], description: 'Traits' },
    ],
  },

  'load-balancer': {
    displayName: 'Load Balancer (Octavia)',
    paths: [
      {
        path: '/v2/**',
        methods: ALL_METHODS,
        description: 'Full access to all Load Balancer APIs',
        isFullAccess: true,
      },
      { path: '/v2/lbaas/loadbalancers', methods: ['GET', 'POST'], description: 'Load balancers' },
      {
        path: '/v2/lbaas/loadbalancers/{loadbalancer_id}',
        methods: ALL_METHODS,
        description: 'Load balancer operations',
      },
      { path: '/v2/lbaas/listeners', methods: ['GET', 'POST'], description: 'Listeners' },
      {
        path: '/v2/lbaas/listeners/{listener_id}',
        methods: ALL_METHODS,
        description: 'Listener operations',
      },
      { path: '/v2/lbaas/pools', methods: ['GET', 'POST'], description: 'Pools' },
      { path: '/v2/lbaas/pools/{pool_id}', methods: ALL_METHODS, description: 'Pool operations' },
      {
        path: '/v2/lbaas/healthmonitors',
        methods: ['GET', 'POST'],
        description: 'Health monitors',
      },
      {
        path: '/v2/lbaas/healthmonitors/{healthmonitor_id}',
        methods: ALL_METHODS,
        description: 'Health monitor operations',
      },
      { path: '/v2/lbaas/l7policies', methods: ['GET', 'POST'], description: 'L7 policies' },
      {
        path: '/v2/lbaas/l7policies/{l7policy_id}',
        methods: ALL_METHODS,
        description: 'L7 policy operations',
      },
      { path: '/v2/lbaas/quotas/{project_id}', methods: ['GET', 'PUT'], description: 'Quotas' },
    ],
  },

  dns: {
    displayName: 'DNS (Designate)',
    paths: [
      {
        path: '/v2/**',
        methods: ALL_METHODS,
        description: 'Full access to all DNS APIs',
        isFullAccess: true,
      },
      { path: '/v2/zones', methods: ['GET', 'POST'], description: 'Zones' },
      { path: '/v2/zones/{zone_id}', methods: ALL_METHODS, description: 'Zone operations' },
      {
        path: '/v2/reverse/floatingips',
        methods: ['GET'],
        description: 'Reverse DNS for floating IPs',
      },
    ],
  },

  orchestration: {
    displayName: 'Orchestration (Heat)',
    paths: [
      {
        path: '/v1/{project_id}/**',
        methods: ALL_METHODS,
        description: 'Full access to all Orchestration APIs',
        isFullAccess: true,
      },
      { path: '/v1/{project_id}/stacks', methods: ['GET', 'POST'], description: 'Stacks' },
      {
        path: '/v1/{project_id}/stacks/{stack_name}/{stack_id}',
        methods: ALL_METHODS,
        description: 'Stack operations',
      },
      { path: '/v1/{project_id}/validate', methods: ['POST'], description: 'Validate template' },
    ],
  },

  'key-manager': {
    displayName: 'Key Manager (Barbican)',
    paths: [
      {
        path: '/v1/**',
        methods: ALL_METHODS,
        description: 'Full access to all Key Manager APIs',
        isFullAccess: true,
      },
      { path: '/v1/secrets', methods: ['GET', 'POST'], description: 'Secrets' },
      {
        path: '/v1/secrets/{secret_id}',
        methods: ['GET', 'DELETE'],
        description: 'Secret operations',
      },
      { path: '/v1/containers', methods: ['GET', 'POST'], description: 'Containers' },
      {
        path: '/v1/containers/{container_id}',
        methods: ['GET', 'DELETE'],
        description: 'Container operations',
      },
      { path: '/v1/orders', methods: ['GET', 'POST'], description: 'Orders' },
      { path: '/v1/cas', methods: ['GET'], description: 'Certificate authorities' },
    ],
  },

  // ============================================
  // Platform9 Custom Services
  // ============================================

  hamgr: {
    displayName: 'HA Manager (Platform9)',
    paths: [
      {
        path: '/hamgr/v1/**',
        methods: ALL_METHODS,
        description: 'Full access to all HA Manager APIs',
        isFullAccess: true,
      },
      { path: '/hamgr/v1/ha', methods: ['GET'], description: 'HA configurations' },
      {
        path: '/hamgr/v1/ha/{aggregate_id}',
        methods: ['GET', 'PUT'],
        description: 'HA configuration operations',
      },
      {
        path: '/hamgr/v1/vmha/retry_evacuation',
        methods: ['POST'],
        description: 'Retry VM HA evacuation',
      },
    ],
  },

  resmgr: {
    displayName: 'Resource Manager (Platform9)',
    paths: [
      {
        path: '/**',
        methods: ALL_METHODS,
        description: 'Full access to all Resource Manager APIs',
        isFullAccess: true,
      },
      { path: '/v1/hosts', methods: ['GET'], description: 'Hosts (v1)' },
      {
        path: '/v1/hosts/{host_id}',
        methods: ['GET', 'DELETE'],
        description: 'Host operations (v1)',
      },
      { path: '/v2/hosts', methods: ['GET'], description: 'Hosts (v2)' },
      { path: '/v2/hosts/{host_id}', methods: ['GET'], description: 'Host operations (v2)' },
      {
        path: '/v2/services/{service}',
        methods: ['GET', 'PUT'],
        description: 'Service configuration',
      },
      { path: '/v2/blueprint', methods: ['GET', 'POST'], description: 'Blueprints' },
      { path: '/v2/blueprint/{name}', methods: ALL_METHODS, description: 'Blueprint operations' },
      { path: '/v2/hostconfigs', methods: ['GET', 'POST'], description: 'Host configurations' },
      {
        path: '/v2/hostconfigs/{id}',
        methods: ALL_METHODS,
        description: 'Host configuration operations',
      },
      { path: '/v2/clusters', methods: ['GET', 'POST'], description: 'Clusters' },
      { path: '/v2/clusters/{name}', methods: ALL_METHODS, description: 'Cluster operations' },
      { path: '/audit/export', methods: ['GET'], description: 'Export audit logs' },
      { path: '/audit/filters', methods: ['GET'], description: 'Audit log filters' },
    ],
  },

  mors: {
    displayName: 'VM Lease Manager (Platform9)',
    paths: [
      {
        path: '/v1/**',
        methods: ALL_METHODS,
        description: 'Full access to all VM Lease Manager APIs',
        isFullAccess: true,
      },
      { path: '/v1/tenant/', methods: ['GET'], description: 'Lease policies' },
      { path: '/v1/tenant/{tenant_id}', methods: ALL_METHODS, description: 'Tenant lease policy' },
    ],
  },

  'instance-ha': {
    displayName: 'Instance HA (Platform9)',
    paths: [
      {
        path: '/v1/{project_id}/**',
        methods: ALL_METHODS,
        description: 'Full access to all Instance HA APIs',
        isFullAccess: true,
      },
      { path: '/v1/{project_id}/segments', methods: ['GET', 'POST'], description: 'HA segments' },
      {
        path: '/v1/{project_id}/segments/{segment_id}',
        methods: ALL_METHODS,
        description: 'HA segment operations',
      },
      {
        path: '/v1/{project_id}/notifications',
        methods: ['GET', 'POST'],
        description: 'HA notifications',
      },
      {
        path: '/v1/{project_id}/notifications/{notification_id}',
        methods: ['GET'],
        description: 'HA notification details',
      },
    ],
  },

  appcatalog: {
    displayName: 'App Catalog (Platform9)',
    paths: [
      {
        path: '/v1/**',
        methods: ALL_METHODS,
        description: 'Full access to all App Catalog APIs',
        isFullAccess: true,
      },
      { path: '/v1/catalog/packages', methods: ['GET', 'POST'], description: 'Catalog packages' },
      {
        path: '/v1/catalog/packages/{package_id}',
        methods: ALL_METHODS,
        description: 'Package operations',
      },
      { path: '/v1/catalog/categories', methods: ['GET', 'POST'], description: 'Categories' },
      {
        path: '/v1/catalog/categories/{category_id}',
        methods: ALL_METHODS,
        description: 'Category operations',
      },
      { path: '/v1/environments', methods: ['GET', 'POST'], description: 'Environments' },
      {
        path: '/v1/environments/{environment_id}',
        methods: ALL_METHODS,
        description: 'Environment operations',
      },
    ],
  },

  'image-cluster': {
    displayName: 'Image Cluster (Platform9)',
    paths: [
      {
        path: '/v2/**',
        methods: ALL_METHODS,
        description: 'Full access to all Image Cluster APIs',
        isFullAccess: true,
      },
      { path: '/v2/images', methods: ['GET'], description: 'Images from cluster' },
      { path: '/v2/images/{image_id}', methods: ['GET'], description: 'Image from cluster' },
    ],
  },

  regioninfo: {
    displayName: 'Region Info (Platform9)',
    paths: [
      {
        path: '/v1/**',
        methods: ALL_METHODS,
        description: 'Full access to all Region Info APIs',
        isFullAccess: true,
      },
      { path: '/v1/regions', methods: ['GET'], description: 'Regions' },
      { path: '/v1/regions/{region_id}', methods: ['GET'], description: 'Region details' },
    ],
  },

  'infra-optim': {
    displayName: 'Infrastructure Optimization (Platform9)',
    paths: [
      {
        path: '/v1/**',
        methods: ALL_METHODS,
        description: 'Full access to all Infrastructure Optimization APIs',
        isFullAccess: true,
      },
      { path: '/v1/goals', methods: ['GET'], description: 'Optimization goals' },
      { path: '/v1/strategies', methods: ['GET'], description: 'Optimization strategies' },
      { path: '/v1/action_plans', methods: ['GET', 'POST'], description: 'Action plans' },
      {
        path: '/v1/action_plans/{action_plan_id}',
        methods: ALL_METHODS,
        description: 'Action plan operations',
      },
      { path: '/v1/audits', methods: ['GET', 'POST'], description: 'Audits' },
      { path: '/v1/audits/{audit_id}', methods: ALL_METHODS, description: 'Audit operations' },
      { path: '/v1/audit_templates', methods: ['GET', 'POST'], description: 'Audit templates' },
      {
        path: '/v1/audit_templates/{audit_template_id}',
        methods: ALL_METHODS,
        description: 'Audit template operations',
      },
    ],
  },

  metering: {
    displayName: 'Metering (Ceilometer)',
    paths: [
      {
        path: '/v2/**',
        methods: ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'],
        description: 'Full access to all Metering APIs',
        isFullAccess: true,
      },
      { path: '/v2/meters', methods: ['GET'], description: 'List meters' },
      { path: '/v2/meters/{meter_name}', methods: ['GET', 'POST'], description: 'Meter samples' },
      { path: '/v2/samples', methods: ['GET'], description: 'Samples' },
      { path: '/v2/resources', methods: ['GET'], description: 'Resources' },
      { path: '/v2/resources/{resource_id}', methods: ['GET'], description: 'Resource details' },
      { path: '/v2/alarms', methods: ['GET', 'POST'], description: 'Alarms' },
      {
        path: '/v2/alarms/{alarm_id}',
        methods: ['GET', 'PUT', 'DELETE'],
        description: 'Alarm operations',
      },
    ],
  },

  metric: {
    displayName: 'Metric (Gnocchi)',
    paths: [
      {
        path: '/v1/**',
        methods: ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'],
        description: 'Full access to all Metric APIs',
        isFullAccess: true,
      },
      { path: '/v1/metric', methods: ['GET', 'POST'], description: 'Metrics' },
      {
        path: '/v1/metric/{metric_id}',
        methods: ['GET', 'DELETE'],
        description: 'Metric operations',
      },
      {
        path: '/v1/metric/{metric_id}/measures',
        methods: ['GET', 'POST'],
        description: 'Metric measures',
      },
      { path: '/v1/resource', methods: ['GET'], description: 'Resources' },
      {
        path: '/v1/resource/{resource_type}',
        methods: ['GET', 'POST'],
        description: 'Resource type operations',
      },
      {
        path: '/v1/resource/{resource_type}/{resource_id}',
        methods: ['GET', 'PATCH', 'DELETE'],
        description: 'Resource operations',
      },
      { path: '/v1/archive_policy', methods: ['GET', 'POST'], description: 'Archive policies' },
      {
        path: '/v1/archive_policy/{archive_policy_name}',
        methods: ['GET', 'PATCH', 'DELETE'],
        description: 'Archive policy operations',
      },
    ],
  },

  cloudformation: {
    displayName: 'CloudFormation (Heat CFN)',
    paths: [
      {
        path: '/v1/**',
        methods: ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'],
        description: 'Full access to all CloudFormation APIs',
        isFullAccess: true,
      },
      { path: '/v1/stacks', methods: ['GET', 'POST'], description: 'List/Create stacks' },
      {
        path: '/v1/stacks/{stack_name}',
        methods: ['GET', 'PUT', 'DELETE'],
        description: 'Stack operations',
      },
      { path: '/v1/stacks/{stack_name}/template', methods: ['GET'], description: 'Stack template' },
      { path: '/v1/stacks/{stack_name}/events', methods: ['GET'], description: 'Stack events' },
      {
        path: '/v1/stacks/{stack_name}/resources',
        methods: ['GET'],
        description: 'Stack resources',
      },
      { path: '/v1/validate', methods: ['POST'], description: 'Validate template' },
    ],
  },
}

/**
 * Get paths for a specific service
 */
export const getPathsForService = (service: string): ApiPathConfig[] => {
  return apiAccessRulesConfig[service]?.paths || []
}

/**
 * Get all available services
 */
export const getAvailableServices = (): string[] => {
  return Object.keys(apiAccessRulesConfig)
}

/**
 * Check if a service has predefined paths
 */
export const hasPredefindedPaths = (service: string): boolean => {
  return service in apiAccessRulesConfig && apiAccessRulesConfig[service].paths.length > 0
}

/**
 * Get default methods for a path
 */
export const getMethodsForPath = (service: string, path: string): AccessRuleMethod[] => {
  const servicePaths = getPathsForService(service)
  const matchedPath = servicePaths.find((p) => p.path === path)
  return matchedPath?.methods || []
}

/**
 * Get the full access path config for a service (first path with isFullAccess: true)
 */
export const getFullAccessPath = (service: string): ApiPathConfig | undefined => {
  const servicePaths = getPathsForService(service)
  return servicePaths.find((p) => p.isFullAccess)
}
